
import { create } from 'zustand';
import type { User } from '@/entities/user';
import {
  KEYCLOAK_GROUP_TO_ROLE,
  REALM_ROLE_TO_APP_ROLE,
  ROLE_PERMISSIONS,
  type AppRole,
} from '@/shared/config';

interface AuthProfile {
  id?: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
  wikiAccess?: boolean;
  toursSeen?: Record<string, number>;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setAuthenticated: (value: boolean) => void;
  setInitialized: (value: boolean) => void;
  setError: (error: string | null) => void;
  syncFromKeycloak: (profile: AuthProfile, groups: string[], effectiveRoles?: AppRole[], permissions?: string[]) => void;
  setAvatarUrl: (url: string | null) => void;
  setTourSeen: (tourId: string, version: number) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: AppRole) => boolean;
  logout: () => void;
}

function parseRolesFromGroups(groups: string[]): AppRole[] {
  const roles = new Set<AppRole>();
  for (const raw of groups) {
    const groupPath = raw.startsWith('/') ? raw.slice(1) : raw;
    const role =
      KEYCLOAK_GROUP_TO_ROLE[groupPath] ??
      KEYCLOAK_GROUP_TO_ROLE[raw] ??
      REALM_ROLE_TO_APP_ROLE[raw] ??
      REALM_ROLE_TO_APP_ROLE[groupPath];
    if (role) roles.add(role);
  }
  if (roles.size === 0) roles.add('user');
  return Array.from(roles);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  error: null,

  setUser: (user) => set({ user }),

  setAvatarUrl: (url) => {
    const { user } = get();
    if (user) set({ user: { ...user, avatarUrl: url } });
  },
  setTourSeen: (tourId, version) => {
    const { user } = get();
    if (!user) return;
    const prev = user.toursSeen?.[tourId] ?? 0;
    set({ user: { ...user, toursSeen: { ...user.toursSeen, [tourId]: Math.max(prev, version) } } });
  },
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setInitialized: (isInitialized) => set({ isInitialized }),
  setError: (error) => set({ error }),

  hasPermission: (permission) => {
    const { user } = get();
    if (!user) return false;
    if (Array.isArray(user.permissions)) return user.permissions.includes(permission);
    for (const role of user.roles) {
      const defaults = (ROLE_PERMISSIONS as Record<string, string[] | undefined>)[role];
      if (defaults?.includes(permission)) return true;
    }
    return false;
  },

  hasRole: (role) => {
    const { user } = get();
    if (!user) return false;
    return user.roles.includes(role);
  },

  logout: () => set({ user: null, isAuthenticated: false }),

  syncFromKeycloak: (
    profile: AuthProfile,
    groups: string[],
    effectiveRoles?: AppRole[],
    permissions?: string[]
  ) => {
    const roles = effectiveRoles ?? parseRolesFromGroups(groups);
    const user: User = {
      id: profile.id || '',
      email: profile.email || profile.username || '',
      name: [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.username || 'User',
      providerId: profile.id,
      avatarUrl: profile.avatarUrl !== undefined ? profile.avatarUrl : get().user?.avatarUrl ?? null,
      wikiAccess: profile.wikiAccess !== undefined ? profile.wikiAccess : get().user?.wikiAccess,
      toursSeen: profile.toursSeen ?? get().user?.toursSeen,
      roles,
      ...(Array.isArray(permissions) ? { permissions } : {}),
    };
    set({ user, isAuthenticated: true });
  },
}));

export const useWikiAccess = (): boolean => useAuthStore((s) => s.user?.wikiAccess !== false);
